import React from 'react';
import { Row, Col, Divider, Tooltip } from 'antd';
import {
  UnitRateType,
  workforceGroupUnitRatesLogType,
} from '@scheduling/graphql';
import { DeprecatedCollapse, Text, Title } from '@common/components';
import { ColorsType } from '@common/tokens';
import { IncentiveRatesViewDrawerUnitRatesRow } from './_incentive-rates-view-drawer-unit-rates-row';

export function IncentiveRatesViewDrawerRatesLog({
  data: {
    workforceGroup,
    organizationUnit,
    effectiveRate,
    noLongerInEffectRates,
    upcomingRates,
  },
}: {
  data: workforceGroupUnitRatesLogType;
}) {
  const noLongerInEffectRatesWithoutEmpty = noLongerInEffectRates.filter(
    ({ type }) => type !== 'empty'
  );

  function getActiveKeysAsList(activeKeysAsObject: Record<string, boolean>) {
    return Object.entries(activeKeysAsObject)
      .filter(([, value]) => value)
      .map(([key]) => key);
  }

  function isLastIndex(array: UnitRateType[], index: number) {
    return array.length - 1 === index;
  }

  function isLastUpcomingRate(index: number) {
    return isLastIndex(upcomingRates, index);
  }

  function isLastNoLongerInEffectRate(index: number) {
    return isLastIndex(noLongerInEffectRatesWithoutEmpty, index);
  }

  const effectiveRatesDisabled = !effectiveRate;

  const defaultActiveKey = getActiveKeysAsList({
    effective: !effectiveRatesDisabled,
    upcoming: upcomingRates.length > 0,
  });

  return (
    <Row gutter={[8, 16]}>
      <Col span={24}>
        <Title>{workforceGroup.name}</Title>
      </Col>
      <Col span={24}>
        <Row>
          <Col span={3}>
            <Label label="Unit" color="midnightLighter" />
          </Col>
          <Col span={21}>{organizationUnit.name}</Col>
        </Row>
      </Col>
      <Col span={24}>
        <Divider />
        <DeprecatedCollapse defaultActiveKey={defaultActiveKey}>
          <DeprecatedCollapse.Panel
            key="effective"
            header={<EffectiveRatesHeader disabled={effectiveRatesDisabled} />}
            collapsible={effectiveRatesDisabled ? 'disabled' : undefined}
          >
            <React.Fragment key={effectiveRate.id}>
              <IncentiveRatesViewDrawerUnitRatesRow unitRates={effectiveRate} />
            </React.Fragment>
          </DeprecatedCollapse.Panel>
          {!!upcomingRates.length && (
            <DeprecatedCollapse.Panel
              key="upcoming"
              header={<Label label="Upcoming Rates" color="elm" />}
            >
              {upcomingRates.map((unitRates, index) => (
                <React.Fragment key={unitRates.id}>
                  <IncentiveRatesViewDrawerUnitRatesRow unitRates={unitRates} />
                  {!isLastUpcomingRate(index) && <Divider />}
                </React.Fragment>
              ))}
            </DeprecatedCollapse.Panel>
          )}
          {!!noLongerInEffectRatesWithoutEmpty.length && (
            <DeprecatedCollapse.Panel
              key="no-longer-in-effect"
              header={<Label label="Inactive Rates" color="elm" />}
            >
              {noLongerInEffectRatesWithoutEmpty.map((unitRates, index) => (
                <React.Fragment key={unitRates.id}>
                  <IncentiveRatesViewDrawerUnitRatesRow unitRates={unitRates} />
                  {!isLastNoLongerInEffectRate(index) && <Divider />}
                </React.Fragment>
              ))}
            </DeprecatedCollapse.Panel>
          )}
        </DeprecatedCollapse>
      </Col>
    </Row>
  );
}

function EffectiveRatesHeader({ disabled }: { disabled: boolean }) {
  const label = (
    <Label label="Active Rates" color={disabled ? 'steelDarker' : 'elm'} />
  );

  if (!disabled) {
    return label;
  }

  return (
    <Tooltip placement="topLeft" title="No active rates available">
      <Col>{label}</Col>
    </Tooltip>
  );
}

function Label({ label, color }: { label: string; color: ColorsType }) {
  return (
    <Text size="lg" color={color}>
      {label}
    </Text>
  );
}
